import { type JSX, useCallback, useEffect, useMemo, useState } from "react";

import * as runtime from "react/jsx-runtime";
import { ErrorBoundary } from "react-error-boundary";
import {
  InsightModel,
  Metadata,
  TimeInterval,
  type TimeSettingsConfig,
  TimeSettingsMode,
  UserPermissions,
} from "@doitintl/cmp-models";
import { getCollection } from "@doitintl/models-any";
import { useCollectionData } from "@doitintl/models-hooks";
import { compile, run } from "@mdx-js/mdx";
import { Box, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useQueries, useQuery } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import orderBy from "lodash/orderBy";

import { useApiContext } from "../../api/context";
import { useCloudConnect } from "../../Components/hooks/useCloudConnect";
import { useAsyncCurrency } from "../../Components/hooks/useCurrency";
import useGenerateReport from "../../Components/hooks/useGenerateReport";
import { queryKeys } from "../../constants";
import { useAuthContext } from "../../Context/AuthContext";
import { useCustomerContext } from "../../Context/CustomerContext";
import { isCustomerInPresentationMode } from "../../Context/useCustomerOrPresentationModeCustomer";
import { useUserContext } from "../../Context/UserContext";
import { type RequestConfig } from "../../Pages/CloudAnalytics/generateReport/types";
import { getReportPath } from "../../Pages/CloudAnalytics/report/utils";
import { AWSFeatureName } from "../../Pages/Settings/AWS/types";
import { consoleErrorWithSentry } from "../../utils";
import { useGetCustomerMPAs } from "../MPA/hooks/useGetCustomerMPAs";
import { getCloudFlowDetails, getInsightCloudFlows, getInsights, getInsightsMissingPermissions } from "./api";
import {
  type CloudFlowDetailsResponse,
  type Insight,
  InsightAccessType,
  type InsightCloudFlows,
  type InsightCloudFlowsResponse,
  type InsightMissingPermissionsResponse,
  InsightProviderId,
  type InsightResultsResponse,
  type NoAccessInsight,
  type NonEntitledInsightsResults,
  type ServerInsight,
} from "./types";
import { normalizeInsightCategories } from "./utils";

export const useMdxContent = (mdxStringifyContent: string) => {
  const [content, setContent] = useState<JSX.Element | null>(null);

  useEffect(() => {
    const compileMdx = async () => {
      const code = String(await compile(mdxStringifyContent, { outputFormat: "function-body" }));
      const { default: Content } = await run(code, { ...runtime, Fragment: Box } as any);

      setContent(
        <ErrorBoundary
          fallback={<p>Error showing content, </p>}
          onError={(error) => {
            consoleErrorWithSentry(error);
          }}
        >
          <Content
            components={{
              // Any headings within the text should be formatted the same way
              h1: ({ children }) => (
                <Typography
                  variant="h4"
                  sx={{
                    mb: 2,
                    mt: 3,
                  }}
                >
                  {children}
                </Typography>
              ),
              h2: ({ children }) => (
                <Typography
                  variant="h4"
                  sx={{
                    mb: 2,
                    mt: 3,
                  }}
                >
                  {children}
                </Typography>
              ),
              h3: ({ children }) => (
                <Typography
                  variant="h4"
                  sx={{
                    mb: 2,
                    mt: 3,
                  }}
                >
                  {children}
                </Typography>
              ),
              h4: ({ children }) => (
                <Typography
                  variant="h4"
                  sx={{
                    mb: 2,
                    mt: 3,
                  }}
                >
                  {children}
                </Typography>
              ),
              h5: ({ children }) => (
                <Typography
                  variant="h4"
                  sx={{
                    mb: 2,
                    mt: 3,
                  }}
                >
                  {children}
                </Typography>
              ),
              h6: ({ children }) => (
                <Typography
                  variant="h4"
                  sx={{
                    mb: 2,
                    mt: 3,
                  }}
                >
                  {children}
                </Typography>
              ),
              p: ({ children, style }) => (
                <Typography variant="body1" style={style} sx={{ mb: 2 }}>
                  {children}
                </Typography>
              ),
              a: ({ children, href }) => (
                <Link target="_blank" href={href}>
                  {children}
                </Link>
              ),
              Table: (props) => <Table sx={{ mb: 2 }} {...props} />,
              TableHead: (props) => <TableHead {...props} />,
              TableBody: (props) => <TableBody {...props} />,
              TableRow: (props) => <TableRow {...props} />,
              TableCell: (props) => <TableCell {...props} />,
            }}
          />
        </ErrorBoundary>
      );
    };

    compileMdx().catch((e) => {
      setContent(e.message);
    });
  }, [mdxStringifyContent]);

  return content;
};

export const defaultTimeSettingsConfig: TimeSettingsConfig = {
  amount: 30,
  mode: TimeSettingsMode.Last,
  unit: TimeInterval.DAY,
  includeCurrent: true,
};

type InsightsState = {
  insights: Insight[] | undefined;
  nonEntitledSummary: NonEntitledInsightsResults | undefined;
  noAccessResults?: NoAccessInsight[];
  isLoading: boolean;
  error: Error | null;
};

export const useInsights = (): InsightsState => {
  const { customerOrPresentationModeCustomer: customer } = useCustomerContext();
  const api = useApiContext();
  const { isDoitEmployee } = useAuthContext();
  const { asyncConvertCurrency } = useAsyncCurrency();
  const [noAccessInsightsWithCurrency, setNoAccessInsightsWithCurrency] = useState<NoAccessInsight[] | undefined>();

  const { isFetching, data, error } = useQuery<
    InsightResultsResponse<ServerInsight>,
    AxiosError,
    InsightResultsResponse
  >({
    queryKey: [customer.id, queryKeys.insights],
    queryFn: () => getInsights(api, { customerId: customer.id }),
    select: (data) => {
      const normalizedResults = normalizeInsightCategories(data.results);
      return { ...data, results: normalizedResults };
    },
    retry: 1,
    retryDelay: 1000,
    retryOnMount: false,

    // Keep the data for an hour for now, as it's unlikely to change often - this way,
    // the user can navigate between insights without getting a loading indicator
    staleTime: 60 * 60 * 1000,
  });

  const [insightsStatusChanges] = useCollectionData(
    getCollection(InsightModel).doc("api").collection("insightsStatusChanges").where("customerId", "==", customer.id)
  );

  useEffect(() => {
    if (!data?.noAccessResults) {
      return;
    }

    const fn = async (insight) => {
      if (!insight.potentialDailySavings) {
        return insight;
      }

      return {
        ...insight,
        potentialDailySavings: {
          ...insight.potentialDailySavings,
          value: await asyncConvertCurrency(insight.potentialDailySavings.value),
        },
      };
    };

    Promise.all(data.noAccessResults.map(fn)).then((noAccessInsightsWithUpdatedCurrency) => {
      setNoAccessInsightsWithCurrency(noAccessInsightsWithUpdatedCurrency);
    });
  }, [asyncConvertCurrency, data?.noAccessResults]);

  const insightsWithStatuses = useMemo(() => {
    if (!data?.results) {
      return [];
    }

    if (!insightsStatusChanges) {
      return data.results;
    }

    return data.results.map((insight) => {
      const matchingStatusChangeDocument = insightsStatusChanges.find(
        (statusChange) => statusChange.insightRef.id === `${insight.customerId}#${insight.providerId}#${insight.key}`
      );

      if (!matchingStatusChangeDocument) {
        const status = insight.displayStatus;

        return {
          ...insight,
          userStatusChanges: { status } as any,
        };
      }
      const sortedStatusChanges = orderBy(matchingStatusChangeDocument.statusChanges, ["timestamp.seconds"], ["desc"]);
      const userStatusChanges = sortedStatusChanges[0];

      return {
        ...insight,
        userStatusChanges,
      };
    });
  }, [data?.results, insightsStatusChanges]);

  // Double safety - the API does not return internal insights for non-DoiT employees,
  // but just in case something goes wrong there, filter them again
  const finalInsights = useMemo(() => {
    if (!isDoitEmployee) {
      return insightsWithStatuses.filter((insight) => !insight.isInternal);
    }
    return insightsWithStatuses;
  }, [insightsWithStatuses, isDoitEmployee]);

  // We are loading if the query is still fetching or if we have data, but have not yet processes the insightStatusChanges
  const isLoading = isFetching || (data !== undefined && insightsStatusChanges === undefined);

  if (isLoading || !data || finalInsights.length === 0) {
    return {
      insights: undefined,
      nonEntitledSummary: undefined,
      noAccessResults: noAccessInsightsWithCurrency,
      error,
      isLoading,
    };
  }

  return {
    insights: finalInsights,
    nonEntitledSummary: data.nonEntitledResultsSummary,
    noAccessResults: noAccessInsightsWithCurrency,
    error,
    isLoading,
  };
};

export const useInsightsInvestigation = ({ insightName }) => {
  const { customer } = useCustomerContext();
  const generateReport = useGenerateReport();
  const formattedCurrentTimestamp = new Date().toLocaleString();
  return useCallback(
    async ({ config }: { config: RequestConfig }) => {
      const params = {
        name: `${insightName} report - ${formattedCurrentTimestamp}`,
        config: {
          ...config,
          // If no type is provided, default to `fixed, which is a "normal" field
          fields: config?.fields.map((field) => ({ ...field, type: field.type ? field.type : Metadata.FIXED })),
          timeSettings: defaultTimeSettingsConfig,
        } satisfies RequestConfig,
        description: "",
      };

      const reportId = await generateReport(params, false);
      const reportPath = getReportPath(customer.id, reportId, { runOnOpen: true });
      return reportPath;
    },
    [customer.id, generateReport, insightName, formattedCurrentTimestamp]
  );
};

type PermissionsByService = Record<InsightProviderId, string[]>;

type UseAWSAccountsWithMissingPermissions = {
  trustedAdvisorAccounts: string[];
  costOptimizationHubAccounts: string[];
  securityHubAccounts: string[];
  isLoading: boolean;
  error: Error | null;
};

const emptyAccountsArray: string[] = [];

// TODO: Remove receivingSecurityInsights attr when we start receiving security-hub insights
export const useAWSAccountsWithMissingPermissions = (
  receivingSecurityInsights?: boolean
): UseAWSAccountsWithMissingPermissions => {
  const api = useApiContext();
  const { customerOrPresentationModeCustomer: customer } = useCustomerContext();
  const { customerMPAs, mpasLoading } = useGetCustomerMPAs(false);

  const { isFetching, data, error } = useQuery<InsightMissingPermissionsResponse, AxiosError, PermissionsByService>({
    queryKey: [customer.id, queryKeys.insightsMissingPermissions],
    queryFn: () => getInsightsMissingPermissions(api, { customerId: customer.id }),
    select: (data) =>
      // Creating PermissionsByProvider structure
      data.reduce((acc, item) => {
        const { providerId, scopes } = item;
        if (!acc[providerId]) {
          acc[providerId] = scopes;
        }
        return acc;
      }, {} as PermissionsByService),
    retry: 1,
    retryDelay: 1000,
    retryOnMount: false,
  });

  // TODO: Calculate directly in the query select and remove the useGetCustomerMPAs dependency, when the API supports security-hub-insights.
  const securityHubAccounts = !receivingSecurityInsights
    ? mpasLoading
      ? emptyAccountsArray
      : customerMPAs?.map((mpa) => mpa.accountNumber).filter(Boolean) || emptyAccountsArray
    : data?.[InsightProviderId.SECURITY_HUB] || emptyAccountsArray;

  return {
    trustedAdvisorAccounts: data?.[InsightProviderId.TRUSTED_ADVISOR] || emptyAccountsArray,
    costOptimizationHubAccounts: data?.[InsightProviderId.COST_OPTIMIZATION_HUB] || emptyAccountsArray,
    securityHubAccounts,
    isLoading: isFetching || mpasLoading,
    error,
  };
};

// TODO: Remove this hook when we receive security-hub-insights permissions from the API
export const useReceivingSecurityHubInsights = (): boolean => {
  const { insights } = useInsights();
  return insights?.some((insight: Insight) => insight.providerId === InsightProviderId.SECURITY_HUB) || false;
};

// TODO: Remove this hook when we receive security-hub-insights permissions from the API
type UseSecurityHubInsightPermissions = {
  isLoading: boolean;
  insightsNum: number;
};
// It checks if the customer receives security hub insights, and if they don't it returns temp info
export const useSecurityHubInsightPermissions = (
  receivingSecurityInsights: boolean,
  hasMissingPermissionAccounts: boolean,
  isLoadingMissingPermissionAccounts: boolean
): UseSecurityHubInsightPermissions => {
  const { customer } = useCustomerContext();

  const [cloudConnect, isLoading] = useCloudConnect(customer);

  const cloudConnectsWithSupportedFeature =
    cloudConnect?.filter(
      (sa) =>
        sa.cloudPlatform === "amazon-web-services" &&
        sa.supportedFeatures?.some((feature) => feature.name === AWSFeatureName.securityHubInsights)
    ) || [];

  if (receivingSecurityInsights || !hasMissingPermissionAccounts || cloudConnectsWithSupportedFeature.length === 0) {
    return {
      isLoading: isLoadingMissingPermissionAccounts,
      insightsNum: 0,
    };
  }

  const cloudConnectsWithEnabledPermissions = cloudConnectsWithSupportedFeature.filter(
    (hasRequiredPermissions) => !hasRequiredPermissions
  );

  let accessType: InsightAccessType;
  if (cloudConnectsWithEnabledPermissions.length === 0) {
    accessType = InsightAccessType.No_access;
  } else if (cloudConnectsWithEnabledPermissions.length === cloudConnectsWithSupportedFeature.length) {
    accessType = InsightAccessType.Full;
  } else {
    accessType = InsightAccessType.Partial;
  }

  const tempSecurityHubInsightsNum = 147;

  return {
    isLoading,
    insightsNum: accessType !== InsightAccessType.Full ? tempSecurityHubInsightsNum : 0,
  };
};

type UseInsightPermissions = {
  noAccessInsights: Insight[];
  missingPermissionsInsights: Insight[];
  grantedPermissionsInsights: Insight[];
};

export const useInsightPermissions = (gkeAccess: InsightAccessType, insights?: Insight[]): UseInsightPermissions => {
  const { customer } = useCustomerContext();
  const isPresentationMode = isCustomerInPresentationMode(customer);

  const categorizedInsights = useMemo(() => {
    const noAccessInsights: Insight[] = [];
    const partialAccessInsights: Insight[] = [];
    const grantedPermissionsInsights: Insight[] = [];

    if (isPresentationMode) {
      return {
        noAccessInsights: [],
        missingPermissionsInsights: [],
        grantedPermissionsInsights: insights || [],
      };
    }

    insights?.forEach((insight) => {
      // TODO: Remove this when we receive FSK8S permissions from the API
      const updatedInsight = { ...insight };
      if (updatedInsight.providerId === InsightProviderId.FSK8S && updatedInsight.cloudTags?.includes("gcp")) {
        updatedInsight.permissions = { accessType: gkeAccess };
      }
      if (!updatedInsight.permissions || updatedInsight.permissions.accessType === InsightAccessType.Full) {
        grantedPermissionsInsights.push(updatedInsight);
      } else if (updatedInsight.permissions.accessType === InsightAccessType.No_access) {
        noAccessInsights.push(updatedInsight);
      } else if (updatedInsight.permissions.accessType === InsightAccessType.Partial) {
        partialAccessInsights.push(updatedInsight);
      }
    });

    const missingPermissionsInsights = [...noAccessInsights, ...partialAccessInsights];

    return {
      noAccessInsights,
      missingPermissionsInsights,
      grantedPermissionsInsights,
    };
  }, [insights, gkeAccess, isPresentationMode]);

  return categorizedInsights;
};

type UseInsightCloudFlows = {
  isLoading: boolean;
  error: Error | null;
  cloudFlows: InsightCloudFlows[];
};

export const useInsightCloudFlows = (insight: Insight = {} as Insight): UseInsightCloudFlows => {
  const api = useApiContext();
  const { userRoles } = useUserContext({ requiredRoles: true, allowNull: true });
  const { isDoitEmployee } = useAuthContext();

  const { key: insightKey, customerId, providerId, cloudFlowBlueprintId, cloudFlowParams } = insight;

  const hasCloudFlowManagerPermission = isDoitEmployee || userRoles?.permissions.has(UserPermissions.CloudFlowManager);
  const insightSupportsCloudFlow = !!cloudFlowBlueprintId && cloudFlowParams?.length > 0;

  const hasCloudFlows = !!insightKey && hasCloudFlowManagerPermission && insightSupportsCloudFlow;

  // Query to get the cloudflow IDs
  const cloudFlowsQuery = useQuery<InsightCloudFlowsResponse, AxiosError>({
    queryKey: [customerId, queryKeys.getInsightCloudFlows, insightKey, "ids"],
    queryFn: () => getInsightCloudFlows(api, { customerId, providerId, insightKey }),
    enabled: hasCloudFlows,
    retry: 1,
    retryDelay: 1000,
    retryOnMount: false,
  });

  // Multi-query to get details for each flow ID
  const flowDetailsQueries = useQueries<CloudFlowDetailsResponse[]>({
    queries: (cloudFlowsQuery.data || []).map(({ id: cloudflowId }) => ({
      queryKey: [customerId, queryKeys.getInsightCloudFlowDetails, insightKey, "details", cloudflowId],
      queryFn: () => getCloudFlowDetails(api, { customerId, cloudflowId }),
      enabled: hasCloudFlows && !!cloudFlowsQuery.data?.length,
      retry: 1,
      retryDelay: 1000,
      retryOnMount: false,
    })),
  });

  const isLoading = (hasCloudFlows && cloudFlowsQuery.isLoading) || flowDetailsQueries.some((query) => query.isLoading);

  const error = cloudFlowsQuery.error || (flowDetailsQueries.find((query) => query.error)?.error as Error);

  const cloudFlows = flowDetailsQueries
    .filter((query) => query.isSuccess)
    .map((query) => {
      const details = query.data as CloudFlowDetailsResponse;

      return {
        id: details.id,
        name: details.name,
        status: details.status,
        createdAt: details.createdAt,
      };
    });

  return {
    isLoading,
    error,
    cloudFlows,
  };
};

type AppcuesFlow = {
  flowId: string;
  flowName: string;
};

// It tracks the Appcues event with the given name and propagates the Appcues flow
export const useAppcuesEventTrack = (flowName: string): AppcuesFlow | undefined => {
  const [flow, setFlow] = useState<AppcuesFlow>();

  useEffect(() => {
    const handler = (flow: AppcuesFlow) => {
      if (flow.flowName === flowName) {
        setFlow(flow);
      }
    };

    // @ts-expect-error : Our git tests don't recognise the types in global.d.ts
    window.Appcues?.on("flow_started", handler);

    return () => {
      // @ts-expect-error
      window.Appcues?.off("flow_started", handler);
    };
  }, [flowName]);

  return flow;
};
